

const koFace = () => import('./ko/face.json');
const enFace = () => import('./en/face.json');
const koLove = () => import('./ko/love.json');
const enLove = () => import('./en/love.json');
const koPremium = () => import('./ko/premium.json');
const enPremium = () => import('./en/premium.json');
const koChugumi = () => import('./ko/chugumi.json');
const enChugumi = () => import('./en/chugumi.json');
const koBeyond = () => import('./ko/beyond.json');
const enBeyond = () => import('./ko/beyond.json'); // Fallback to KO for now as EN doesn't exist
const koInside = () => import('./ko/inside.json');
const enInside = () => import('./ko/inside.json');

const contentCache = {
    ko: {},
    en: {}
};

/**
 * Load domain content for a specific locale
 */
export const loadContent = async (locale, domain) => {
    if (contentCache[locale][domain]) return contentCache[locale][domain];

    try {
        let module;
        if (locale === 'en') {
            if (domain === 'face') module = await enFace();
            else if (domain === 'love') module = await enLove();
            else if (domain === 'premium') module = await enPremium();
            else if (domain === 'premium') module = await enPremium();
            else if (domain === 'chugumi') module = await enChugumi();
            else if (domain === 'beyond') module = await enBeyond();
            else if (domain === 'inside') module = await enInside();
        } else {
            if (domain === 'face') module = await koFace();
            else if (domain === 'love') module = await koLove();
            else if (domain === 'premium') module = await koPremium();
            else if (domain === 'chugumi') module = await koChugumi();
            else if (domain === 'beyond') module = await koBeyond();
            else if (domain === 'inside') module = await koInside();
        }
        contentCache[locale][domain] = module.default;
        return module.default;
    } catch (e) {
        console.error(`Failed to load content: ${locale}/${domain}`, e);
        return null;
    }
};

/**
 * Get content by key with fallback and params
 * key format: "domain.sub.sub.target"
 */
export const getContent = (locale, fullKey, params = {}, contents = {}) => {
    const parts = fullKey.split('.');
    const domain = parts[0];
    const path = parts.slice(1);

    const domainContent = contents[domain] || contentCache[locale][domain];

    let result = domainContent;
    for (const part of path) {
        if (result && result[part] !== undefined) {
            result = result[part];
        } else {
            result = null;
            break;
        }
    }

    // Fallback to KO if EN is missing
    if (result === null && locale === 'en') {
        const koDomainContent = contentCache['ko'][domain];
        if (koDomainContent) {
            result = koDomainContent;
            for (const part of path) {
                if (result && result[part] !== undefined) {
                    result = result[part];
                } else {
                    result = null;
                    break;
                }
            }
        }
    }

    if (result === null) {
        return process.env.NODE_ENV === 'development' ? `[[missing: ${fullKey}]]` : '';
    }

    // If result is an object (label/desc/etc), we return it as is or process its strings
    if (typeof result === 'object' && result !== null) {
        if (Array.isArray(result)) {
            return result.map(v => (typeof v === 'string' ? interpolate(v, params) : v));
        }
        const processed = {};
        for (const [k, v] of Object.entries(result)) {
            if (typeof v === 'string') {
                processed[k] = interpolate(v, params);
            } else {
                processed[k] = v;
            }
        }
        return processed;
    }

    if (typeof result === 'string') {
        return interpolate(result, params);
    }

    return result;
};

function interpolate(text, params) {
    return text.replace(/\{\{(.*?)\}\}/g, (match, p1) => {
        const key = p1.trim();
        return params[key] !== undefined ? params[key] : match;
    });
}

// Hook for using content in React
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const useContent = (domains = []) => {
    const { i18n } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [contents, setContents] = useState({});

    const locale = i18n.language.startsWith('en') ? 'en' : 'ko';

    useEffect(() => {
        let isMounted = true;
        const fetch = async () => {
            const newContents = {};
            for (const domain of domains) {
                newContents[domain] = await loadContent(locale, domain);
                // Also load KO as fallback
                if (locale === 'en') {
                    await loadContent('ko', domain);
                }
            }
            if (isMounted) {
                setContents(newContents);
                setLoaded(true);
            }
        };
        fetch();
        return () => { isMounted = false; };
    }, [locale, JSON.stringify(domains)]);

    const getC = (key, params = {}) => getContent(locale, key, params, contents);

    return { getC, loaded };
};
